import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * SuperAwesome AwesomeAds integration scaffolding.
 *
 * Configure via `frontend/.env`:
 *   REACT_APP_AWESOMEADS_PUBLISHER_ID=<your publisher id>
 *   REACT_APP_AWESOMEADS_ENABLED=1
 *
 * Until those are set, AdSlot renders a clearly-labelled placeholder so the
 * layout reserves the correct space. Zone IDs map to ad placements you have
 * configured in your AwesomeAds dashboard.
 */
const SDK_URL = "https://ads.superawesome.tv/v2/sdk.js";
let _sdkLoading = null;

function loadSdk() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.SA && window.SA.ads) return Promise.resolve(window.SA.ads);
  if (_sdkLoading) return _sdkLoading;
  _sdkLoading = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.SA?.ads || null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return _sdkLoading;
}

const ENABLED = process.env.REACT_APP_AWESOMEADS_ENABLED === "1";
const PUBLISHER_ID = process.env.REACT_APP_AWESOMEADS_PUBLISHER_ID;

export function AdSlot({ zoneId, size = "banner", className }) {
  const [loaded, setLoaded] = useState(false);
  const containerId = `ad-slot-${zoneId}`;

  useEffect(() => {
    let cancelled = false;
    if (!ENABLED || !PUBLISHER_ID || !zoneId) return;
    loadSdk().then((sdk) => {
      if (cancelled || !sdk) return;
      try {
        sdk.loadAd({
          placementId: zoneId,
          publisherId: PUBLISHER_ID,
          container: `#${containerId}`,
        });
        setLoaded(true);
      } catch {
        /* ignore */
      }
    });
    return () => { cancelled = true; };
  }, [zoneId, containerId]);

  const heightClass = size === "leaderboard" ? "h-[90px]" : size === "rectangle" ? "h-[250px]" : "h-[60px]";

  return (
    <div
      id={containerId}
      data-testid={`ad-slot-${zoneId}`}
      className={cn(
        "w-full rounded-lg border border-dashed border-border/60 bg-muted/30 flex items-center justify-center text-xs text-muted-foreground overflow-hidden",
        heightClass,
        className
      )}
    >
      {!loaded && (
        <span>
          {ENABLED && PUBLISHER_ID
            ? "Loading ad…"
            : "Ad space (SuperAwesome AwesomeAds)"}
        </span>
      )}
    </div>
  );
}
